import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  casesService,
  createCaseListNavigationParams,
  type CaseAdjacentDirection,
  type CaseFilters,
} from "../services/cases.service";

interface AdjacentQueryResult {
  adjacentCaseId: string | null;
}

export function useCaseAdjacentNavigation(
  caseId: string | undefined,
  filters: CaseFilters | null
) {
  const navigate = useNavigate();
  const [navigatingDirection, setNavigatingDirection] =
    useState<CaseAdjacentDirection | null>(null);
  const enabled = Boolean(caseId && filters);
  const queryKeyContext = useMemo(
    () => (filters ? createCaseListNavigationParams(filters).toString() : ""),
    [filters]
  );
  const queryOptions = useMemo(
    () => ({
      enabled,
      staleTime: 0,
      queryFn: ({ queryKey }: { queryKey: readonly unknown[] }) =>
        casesService.getAdjacent(
          caseId!,
          queryKey[2] as CaseAdjacentDirection,
          filters!
        ),
    }),
    [caseId, enabled, filters]
  );
  const previousQuery = useQuery<AdjacentQueryResult>({
    queryKey: ["case-adjacent", caseId, "previous", queryKeyContext],
    ...queryOptions,
  });
  const nextQuery = useQuery<AdjacentQueryResult>({
    queryKey: ["case-adjacent", caseId, "next", queryKeyContext],
    ...queryOptions,
  });

  const navigateToAdjacent = useCallback(
    async (direction: CaseAdjacentDirection) => {
      if (!caseId || !filters) return;
      setNavigatingDirection(direction);
      try {
        const result = await casesService.getAdjacent(
          caseId,
          direction,
          filters
        );
        if (!result.adjacentCaseId) return;
        const params = createCaseListNavigationParams(filters);
        navigate(`/cases/${result.adjacentCaseId}?${params.toString()}`);
      } finally {
        setNavigatingDirection(null);
      }
    },
    [caseId, filters, navigate]
  );

  const previousDisabled =
    !enabled ||
    previousQuery.isLoading ||
    previousQuery.data?.adjacentCaseId === null;
  const nextDisabled =
    !enabled || nextQuery.isLoading || nextQuery.data?.adjacentCaseId === null;

  return {
    enabled,
    previousDisabled,
    nextDisabled,
    previousLoading: navigatingDirection === "previous",
    nextLoading: navigatingDirection === "next",
    onPrevious: useCallback(
      () => navigateToAdjacent("previous"),
      [navigateToAdjacent]
    ),
    onNext: useCallback(() => navigateToAdjacent("next"), [navigateToAdjacent]),
  };
}
